import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { useTraditionalCharactersEnabled } from "@-/dev.improve.haniverse.lib.posthog";

export type OnboardingLearningProgress = {
  /** At least one character has been learned (or mastered or skipped). */
  hasLearnedCharacter: boolean;
  /** Every character of the first level has been learned (or mastered or skipped). */
  hasLearnedFirstLevel: boolean;
};

export type OnboardingLearnedCard = {
  userGraphemeMeaningCard: { grapheme: string } | null;
};

/** What the learner's learned meaning cards say about the onboarding's unlocks. */
export function getOnboardingLearningProgress(
  cards: readonly OnboardingLearnedCard[],
  firstLevelCharacters: readonly string[],
): OnboardingLearningProgress {
  const learned = new Set<string>();
  for (const card of cards) {
    if (card.userGraphemeMeaningCard !== null) {
      learned.add(card.userGraphemeMeaningCard.grapheme);
    }
  }
  return {
    hasLearnedCharacter: learned.size > 0,
    hasLearnedFirstLevel:
      firstLevelCharacters.length > 0 &&
      firstLevelCharacters.every((character) => learned.has(character)),
  };
}

function getFirstLevelCharacters(traditional: boolean): readonly string[] {
  const [firstLevel] = traditional ? getTraditionalLevels() : getSimplifiedLevels();
  return firstLevel?.characters ?? [];
}

/**
 * The learner's progress as the onboarding counts it, read only while `enabled` (a new learner,
 * whose learned cards are few). `null` until it has loaded, and while disabled.
 */
export function useOnboardingLearningProgress({
  enabled,
}: {
  enabled: boolean;
}): OnboardingLearningProgress | null {
  const traditional = useTraditionalCharactersEnabled();
  const cardsOptions = actorQueries(import.meta.location).userCard.findMany.useQueryOptions({
    where: {
      OR: [
        { startedStudyingAt: { isNotNull: true } },
        { manuallyMasteredAt: { isNotNull: true } },
        { skippedAt: { isNotNull: true } },
      ],
    },
    with: { userGraphemeMeaningCard: true },
  });
  // The app shell is always on screen, so this watches regardless of which tab is focused.
  const cards = useQuery({ ...cardsOptions, enabled, subscribed: enabled });
  if (!enabled || !cards.isSuccess) {
    return null;
  }
  return getOnboardingLearningProgress(cards.data, getFirstLevelCharacters(traditional));
}
